/**
 * PATH: packages/ui/src/components/sidebar/sidebar-helpers.tsx
 * PURPOSE: Shared helpers and small components used across sidebar sub-components
 */

import React from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip'
import type { NavItem, BadgeVariant } from '../types'

export function getBadgeClasses(variant?: BadgeVariant) {
  switch (variant) {
    case 'warning':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300'
    case 'destructive':
      return 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
    default:
      return 'bg-border text-foreground'
  }
}

export function getDisabledClasses(item: NavItem, _collapsed: boolean) {
  if (!item.disabled) return ''
  if (item.premiumOnly) {
    return 'opacity-60 cursor-not-allowed bg-amber-50/50 dark:bg-amber-900/10'
  }
  return 'opacity-40 cursor-not-allowed text-muted-foreground'
}

export function DisabledTooltipWrapper({
  item,
  children,
  collapsed,
}: {
  item: NavItem
  children: React.ReactNode
  collapsed: boolean
}) {
  if (!item.disabled || !item.disabledReason) {
    return <>{children}</>
  }

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="w-full">{children}</div>
        </TooltipTrigger>
        <TooltipContent
          side={collapsed ? 'right' : 'top'}
          className="max-w-[200px] text-xs"
        >
          <div className="space-y-1">
            <p className="font-medium text-foreground">
              {item.premiumOnly ? '🔒 Premium Feature' : '🚫 Access Restricted'}
            </p>
            <p className="text-muted-foreground">
              {item.disabledReason}
            </p>
            {item.premiumOnly && (
              <button className="mt-1 text-[10px] px-2 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 rounded-full hover:bg-amber-200 dark:hover:bg-amber-900/60 transition-colors">
                Upgrade to unlock
              </button>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
